import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { settingsApi } from '@/api/settings';
import { Loading } from '@/components/ui';

const FLAG_KEY = 'ENFORCE_IMPERSONATION_CONSENT';

export default function ImpersonationPolicySection() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings-impersonation-policy'],
    queryFn: async () => {
      const flags = await settingsApi.listFeatureFlags();
      return flags.find((flag) => flag.key === FLAG_KEY) ?? null;
    },
    staleTime: 15_000,
  });

  const updateMutation = useMutation({
    mutationFn: async (checked: boolean) =>
      settingsApi.upsertFeatureFlag(FLAG_KEY, checked),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings-feature-flags'] });
      await queryClient.invalidateQueries({ queryKey: ['settings-impersonation-policy'] });
    },
  });

  if (isLoading) {
    return (
      <section className="card p-6">
        <Loading text="Loading impersonation policy..." />
      </section>
    );
  }

  const enabled = data ? Boolean(data.value) : false;

  return (
    <section className="card p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-red-500/10 p-2 text-red-400">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-ink-primary">Impersonation policy</h2>
          <p className="mt-1 text-sm text-ink-secondary">
            Controls whether owner impersonation is blocked when the customer has not granted consent.
          </p>
        </div>
      </div>

      <label className="flex items-center justify-between gap-4 rounded-lg border border-navy-border bg-navy/30 p-4">
        <div>
          <p className="text-sm font-medium text-ink-primary">Enforce customer consent</p>
          <p className="mt-1 text-xs text-ink-secondary">
            Stored as the <code className="text-gold">{FLAG_KEY}</code> feature flag and read by the Edge function at request time.
          </p>
        </div>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => void updateMutation.mutateAsync(event.target.checked)}
          disabled={updateMutation.isPending}
          className="h-5 w-5"
        />
      </label>
    </section>
  );
}
